/**
 * Gallery — slim orchestrator (Phase 6 refactor).
 *
 * Owns state + side-effects; delegates rendering to children via a
 * `ctx` prop bag.
 *
 *   - GalleryStatsCards       (4 KPI tiles — Total / Visuals / Tables / Insights)
 *   - GalleryFilters          (search + type + workspace + last-modified + view toggle)
 *   - GalleryListView         (table / tile render of items)
 *   - GalleryPagination       (page-number nav)
 *   - GalleryPreviewDialog    (preview + add-to-dashboard)
 *   - GalleryShareDialog      (direct-share dialog)
 *
 * iter-121.47 — Visual redesign to match the modernized Gallery mockup.
 * Existing functionality (search, kind filter, share, delete, SA bulk-
 * delete, deep-link preview, gallery:templates-changed broadcast,
 * cross-tenant shared items, add-to-dashboard) is preserved verbatim.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { useConfirm } from "@/lib/confirm-context";
import { useViewMode } from "@/components/ViewToggle";
import { galleryItemToWidget } from "@/components/dashboards/GalleryPickerDialog";
import GalleryListView from "@/components/gallery/GalleryListView";
import GalleryPreviewDialog from "@/components/gallery/GalleryPreviewDialog";
import GalleryShareDialog from "@/components/gallery/GalleryShareDialog";
import GalleryStatsCards, { STAT_DEFS } from "@/components/gallery/GalleryStatsCards";
import GalleryFilters from "@/components/gallery/GalleryFilters";
import GalleryPagination from "@/components/gallery/GalleryPagination";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const MODIFIED_CUTOFFS = {
    any: null,
    "24h": 24 * 60 * 60 * 1000,
    "7d":   7 * 24 * 60 * 60 * 1000,
    "30d": 30 * 24 * 60 * 60 * 1000,
    "90d": 90 * 24 * 60 * 60 * 1000,
};

export default function Gallery() {
    const { user } = useAuth();
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [workspaces, setWorkspaces] = useState([]);
    const [q, setQ] = useState("");
    const [kind, setKind] = useState("all");
    const [statFilter, setStatFilter] = useState("all"); // KPI tile → group filter
    const [workspaceFilter, setWorkspaceFilter] = useState("all");
    const [modifiedFilter, setModifiedFilter] = useState("any");
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useViewMode("gallery");
    const [preview, setPreview] = useState(null);
    const [dashboards, setDashboards] = useState([]);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const isSuperAdmin = user?.role === "super_admin";

    const canDelete = (item) => (
        user && (
            item.created_by === user.id ||
            user.role === "super_admin" ||
            (user.role === "org_admin" && item.tenant_id === user.tenant_id)
        )
    );
    const canShare = canDelete;
    const [shareItem, setShareItem] = useState(null);

    const load = async () => {
        const r = await api.get("/gallery");
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    // Preload workspaces so the filter dropdown + workspace column can
    // resolve `workspace_id` → friendly name.
    useEffect(() => {
        api.get("/workspaces")
           .then((r) => setWorkspaces(r.data || []))
           .catch(() => setWorkspaces([]));
    }, []);

    // iter-119u (C2): refetch when SA toggles template enablement.
    useEffect(() => {
        const onChanged = () => load();
        window.addEventListener("gallery:templates-changed", onChanged);
        return () => window.removeEventListener("gallery:templates-changed", onChanged);
    }, []);

    // Deep-link from Workspace → /gallery?preview=<id>.
    const [params, setParams] = useSearchParams();
    const nav = useNavigate();
    useEffect(() => {
        const pid = params.get("preview");
        if (!pid) return;
        const inList = items.find((i) => i.id === pid);
        if (inList) { setPreview(inList); return; }
        api.get(`/gallery/${pid}`)
           .then((r) => setPreview(r.data))
           .catch(() => toast.error("You don't have access to that asset."));
    }, [params, items]);

    useEffect(() => {
        if (preview === null && params.get("preview")) {
            const next = new URLSearchParams(params);
            next.delete("preview");
            setParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview]);

    useEffect(() => {
        api.get("/dashboards").then((r) => setDashboards(r.data || [])).catch(() => setDashboards([]));
    }, []);

    const addToDashboard = async (dashboardId) => {
        const item = preview;
        if (!item || !dashboardId) return;
        const widget = galleryItemToWidget(item);
        if (!widget) { toast.error("This gallery item cannot be added as a widget"); return; }
        const target = dashboards.find((d) => d.id === dashboardId);
        const ok = await confirm({
            title: `Add "${item.name}" to "${target?.name || "dashboard"}"?`,
            description: target?.attached_sources?.length
                ? `The widget will appear on the dashboard immediately and use its connected data.`
                : `Note: this dashboard has no data sources attached yet. The widget may render empty until you attach a compatible source.`,
            confirmLabel: "Add to dashboard",
        });
        if (!ok) return;
        try {
            const d = await api.get(`/dashboards/${dashboardId}`);
            const widgets = [...(d.data.widgets || []), widget];
            const existingBindings = d.data.data_model_bindings || [];
            const incoming = item.data_model_bindings || widget.data_model_bindings || [];
            const bindingKey = (b) => (typeof b === "string" ? b : (b?.model_id || ""));
            const seen = new Set(existingBindings.map(bindingKey));
            const merged = [...existingBindings];
            for (const b of incoming) {
                const k = bindingKey(b);
                if (k && !seen.has(k)) { merged.push(b); seen.add(k); }
            }
            const patch = { widgets };
            if (merged.length > existingBindings.length) patch.data_model_bindings = merged;
            await api.patch(`/dashboards/${dashboardId}`, patch);
            toast.success(`Added "${item.name}" to "${d.data.name}"`, {
                description: "Open the dashboard to see it.",
                action: { label: "Open", onClick: () => nav(`/dashboards/${dashboardId}`) },
                duration: 6000,
            });
            setPreview(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add");
        }
    };

    const del = async (item) => {
        const ok = await confirm({
            title: `Delete "${item.name}"?`,
            description: "This permanently removes the gallery item from the shared catalog. This cannot be undone.",
            confirmLabel: "Delete item",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/gallery/${item.id}`);
            toast.success("Removed");
            window.dispatchEvent(new CustomEvent("gallery:templates-changed", {
                detail: { id: item.id, action: "deleted" },
            }));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const bulkDelete = async () => {
        if (!isSuperAdmin || selectedIds.size === 0) return;
        const count = selectedIds.size;
        const ok = await confirm({
            title: `Delete ${count} item${count !== 1 ? "s" : ""}?`,
            description: "This permanently removes the selected gallery items from the shared catalog. Any dashboards instantiated from a deleted prebuilt template will also be removed across every tenant. This cannot be undone.",
            confirmLabel: `Delete ${count}`,
            destructive: true,
        });
        if (!ok) return;
        try {
            const r = await api.post("/gallery/bulk-delete", { ids: Array.from(selectedIds) });
            const cascaded = r.data?.cascaded_dashboards || 0;
            toast.success(
                `Removed ${r.data?.deleted || count} item${(r.data?.deleted || count) !== 1 ? "s" : ""}`,
                cascaded > 0 ? { description: `Cascaded ${cascaded} instantiated dashboard${cascaded !== 1 ? "s" : ""}` } : undefined,
            );
            setSelectedIds(new Set());
            window.dispatchEvent(new CustomEvent("gallery:templates-changed", {
                detail: { ids: Array.from(selectedIds), action: "bulk-deleted" },
            }));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk delete failed");
        }
    };

    const toggleOne = (id) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleAllFiltered = (filteredList) => setSelectedIds((prev) => {
        const allIds = filteredList.map((i) => i.id);
        const allSelected = allIds.length > 0 && allIds.every((id) => prev.has(id));
        if (allSelected) {
            const next = new Set(prev);
            allIds.forEach((id) => next.delete(id));
            return next;
        }
        const next = new Set(prev);
        allIds.forEach((id) => next.add(id));
        return next;
    });

    // Stats are computed on the FULL set minus prebuilt-dashboard items
    // (those live on the Dashboards page). The KPI tiles always reflect
    // the universe so the user sees total counts even after filtering.
    const universe = useMemo(
        () => items.filter((i) => i.kind !== "prebuilt_dashboard"),
        [items],
    );

    // The filtered list applied to the table/tile body. Order of filters:
    // stat tile → kind dropdown → workspace dropdown → modified dropdown →
    // free-text search.
    const filteredAll = useMemo(() => {
        const statDef = STAT_DEFS.find((d) => d.key === statFilter);
        const cutoff = MODIFIED_CUTOFFS[modifiedFilter];
        const now = Date.now();
        const lcq = (q || "").trim().toLowerCase();
        return universe.filter((i) => {
            if (statDef?.kinds && !statDef.kinds.includes(i.kind)) return false;
            if (kind !== "all" && i.kind !== kind) return false;
            if (workspaceFilter !== "all") {
                if (workspaceFilter === "__none__") {
                    if (i.workspace_id) return false;
                } else if (i.workspace_id !== workspaceFilter) return false;
            }
            if (cutoff && i.created_at) {
                const ts = new Date(i.created_at).getTime();
                if (Number.isNaN(ts) || now - ts > cutoff) return false;
            }
            if (lcq && !(
                (i.name || "").toLowerCase().includes(lcq) ||
                (i.description || "").toLowerCase().includes(lcq) ||
                (i.tags || []).some((t) => t.toLowerCase().includes(lcq))
            )) return false;
            return true;
        });
    }, [universe, statFilter, kind, workspaceFilter, modifiedFilter, q]);

    // Reset to page 1 whenever any filter changes; selection follows the filters.
    useEffect(() => { setPage(1); }, [q, kind, statFilter, workspaceFilter, modifiedFilter, viewMode]);
    useEffect(() => { setSelectedIds(new Set()); }, [q, kind, statFilter, workspaceFilter, modifiedFilter]);

    const pageItems = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredAll.slice(start, start + PAGE_SIZE);
    }, [filteredAll, page]);

    const clearFilters = () => {
        setQ("");
        setKind("all");
        setWorkspaceFilter("all");
        setModifiedFilter("any");
        setStatFilter("all");
    };

    const listCtx = {
        filtered: pageItems, allFiltered: filteredAll, viewMode, isSuperAdmin, selectedIds,
        setPreview, toggleOne, toggleAllFiltered,
        canShare, canDelete, setShareItem, del, workspaces,
    };
    const previewCtx = {
        preview, setPreview, dashboards, addToDashboard, canShare, setShareItem,
    };

    return (
        <div className="fadein">
            <PageHeader
                eyebrow="Reusable assets"
                title="Gallery"
                description="View, manage, and organize your gallery assets."
                testId="gallery-header"
            />
            <PageContent>
                <GalleryFilters
                    q={q} setQ={setQ}
                    kind={kind} setKind={setKind}
                    workspace={workspaceFilter} setWorkspace={setWorkspaceFilter} workspaces={workspaces}
                    modified={modifiedFilter} setModified={setModifiedFilter}
                    viewMode={viewMode} setViewMode={setViewMode}
                    onClear={clearFilters}
                />

                <GalleryStatsCards
                    items={universe}
                    activeStat={statFilter}
                    onSelectStat={setStatFilter}
                />

                {/* SA-only bulk-delete toolbar */}
                {isSuperAdmin && selectedIds.size > 0 && (
                    <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2" data-testid="gallery-bulk-toolbar">
                        <div className="text-xs">
                            <span className="font-medium" data-testid="gallery-selected-count">{selectedIds.size}</span> of {filteredAll.length} item{filteredAll.length !== 1 ? "s" : ""} selected
                            <button
                                onClick={() => setSelectedIds(new Set())}
                                className="ml-3 text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground"
                                data-testid="gallery-bulk-clear"
                            >Deselect all</button>
                        </div>
                        <Button variant="destructive" size="sm" className="h-8 rounded-md" onClick={bulkDelete} data-testid="gallery-bulk-delete">
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete selected
                        </Button>
                    </div>
                )}

                <GalleryListView ctx={listCtx} />

                {filteredAll.length > 0 && (
                    <GalleryPagination
                        page={page}
                        pageSize={PAGE_SIZE}
                        totalCount={filteredAll.length}
                        onPageChange={setPage}
                    />
                )}
            </PageContent>

            <GalleryPreviewDialog ctx={previewCtx} />
            <GalleryShareDialog item={shareItem} onClose={() => setShareItem(null)} />
        </div>
    );
}
